import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useUser } from "../hooks/useUser";
import { useAuthModal } from "../App";
import { GAMES } from "../config/games";
import SettingsModal from "./SettingsModal";
import '../styles/Navbar.css';

const GAME_LINKS = GAMES
  .filter(g => g.available)
  .map(g => ({ path: g.path, label: g.navLabel }))

const OTHER_LINKS = [
  { path: "/leaderboard", label: "Leaderboard" },
  { path: "/feedback",    label: "Feedback"    },
];

export default function Navbar() {
  const { user, profile, logout } = useUser()
  const { open: openAuth } = useAuthModal()
  const navigate = useNavigate()
  const location = useLocation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [logoutConfirm, setLogoutConfirm] = useState(false)
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const mobileRef = useRef(null)
  const themeDropdownRef = useRef(null)

  const [theme, setTheme] = useState(() =>
    localStorage.getItem('azerodle-theme') || document.documentElement.dataset.theme || 'dark'
  )
  const applyTheme = (t) => {
    document.documentElement.dataset.theme = t
    localStorage.setItem('azerodle-theme', t)
    setTheme(t)
    setThemeDropdownOpen(false)
  }

  const handleLogout = () => { logout(); navigate("/"); setMobileOpen(false); setLogoutConfirm(false) }
  const requestLogout = () => { setDropdownOpen(false); setLogoutConfirm(true) }

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target)) {
        setMobileOpen(false)
      }
      if (themeDropdownRef.current && !themeDropdownRef.current.contains(e.target)) {
        setThemeDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  return (
    <>
    <nav className="navbar">

      {/* ── Mobile hamburger (left) ── */}
      <div className="navbar-mobile-left" ref={mobileRef}>
        <button
          className={`navbar-hamburger ${mobileOpen ? "open" : ""}`}
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Menu"
        >
          <span /><span /><span />
        </button>

        {mobileOpen && (
          <div className="navbar-mobile-menu">
            <p className="navbar-mobile-section">Games</p>
            {GAME_LINKS.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar-mobile-item ${location.pathname === link.path ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >{link.label}</Link>
            ))}
            <div className="navbar-mobile-divider" />
            <p className="navbar-mobile-section">More</p>
            {OTHER_LINKS.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar-mobile-item ${location.pathname === link.path ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >{link.label}</Link>
            ))}
          </div>
        )}
      </div>

      <div className="navbar-logo-wrap">
        <Link to="/" className="navbar-logo">AZERODLE</Link>
      </div>

      {/* Game links — desktop only */}
      <div className="navbar-links navbar-links-games">
        {GAME_LINKS.map(link => (
          <Link
            key={link.path}
            to={link.path}
            className={`navbar-link game-link ${location.pathname === link.path ? "active" : ""}`}
          >
            {link.label}
          </Link>
        ))}
      </div>

      {/* Other links — desktop only */}
      <div className="navbar-links navbar-links-other">
        {OTHER_LINKS.map(link => (
          <Link
            key={link.path}
            to={link.path}
            className={`navbar-link other-link ${location.pathname === link.path ? "active" : ""}`}
          >
            {link.label}
          </Link>
        ))}
      </div>

      <div className="navbar-right">
        {user ? (
          <div className="navbar-user" ref={dropdownRef}>
            <button className="navbar-user-btn" onClick={() => setDropdownOpen(prev => !prev)}>
              <svg className={`navbar-chevron ${dropdownOpen ? "open" : ""}`} width="10" height="10" viewBox="0 0 10 6" fill="none">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span className="navbar-username">{profile?.username || "..."}</span>
              <div className="navbar-avatar">
                {profile?.avatar_url ? <img src={profile.avatar_url} alt="avatar" /> : "🧙"}
              </div>
            </button>
            {dropdownOpen && (
              <div className="navbar-dropdown">
                <Link to="/profile" className="navbar-dropdown-item" onClick={() => setDropdownOpen(false)}>
                  Profile
                </Link>
                <button
                  className="navbar-dropdown-item"
                  onClick={() => { setDropdownOpen(false); setSettingsOpen(true) }}
                >
                  Settings
                </button>
                <div className="navbar-dropdown-divider" />
                <button className="navbar-dropdown-item logout" onClick={requestLogout}>Log out</button>
              </div>
            )}
          </div>
        ) : (
          <div className="navbar-guest-actions">
            <button className="navbar-auth-btn login" onClick={openAuth}>Log In</button>
            <div className="navbar-theme-wrap" ref={themeDropdownRef}>
              <button
                className="navbar-theme-btn"
                onClick={() => setThemeDropdownOpen(prev => !prev)}
                aria-label="Theme"
              >⚙</button>
              {themeDropdownOpen && (
                <div className="navbar-theme-dropdown">
                  <p className="navbar-theme-label">Theme</p>
                  <button
                    className={`navbar-theme-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => applyTheme('dark')}
                  >Dark</button>
                  <button
                    className={`navbar-theme-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => applyTheme('light')}
                  >Light</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </nav>

    {/* ── Settings modal — portal to body ── */}
    {settingsOpen && createPortal(
      <SettingsModal onClose={() => setSettingsOpen(false)} />,
      document.body
    )}

    {/* ── Logout confirm — portal to body ── */}
    {logoutConfirm && createPortal(
      <div className="navbar-confirm-backdrop">
        <div className="navbar-confirm-box">
          <div className="navbar-confirm-inner">
            <p className="navbar-confirm-title">Leaving already?</p>
            <p className="navbar-confirm-text">Are you sure you want to log out?</p>
            <div className="navbar-confirm-actions">
              <button className="navbar-confirm-btn confirm" onClick={handleLogout}>Log out</button>
              <button className="navbar-confirm-btn cancel" onClick={() => setLogoutConfirm(false)}>Stay</button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )}
  </>
  )
}